import { z } from 'zod';

import {
  DataSourceSchema,
  ErrorCodeSchema,
  INDEX_CODES,
  IndexCodeSchema,
  SECTOR_IDS,
  WINDOW_KEYS,
  WindowKeySchema,
  type ErrorCode,
} from './enums';
import { TickerSchema } from './primitives';
import { SectorSchema } from './sector.schema';

export const ErrorDetailSchema = z.object({
  path: z.string().max(200),
  message: z.string().max(500),
});
export type ErrorDetail = z.infer<typeof ErrorDetailSchema>;

export const ERROR_DETAILS_MAX = 50;

const ErrorDetailsSchema = z.array(ErrorDetailSchema).max(ERROR_DETAILS_MAX).optional();

/**
 * Codes whose bodies must be byte-identical across reasons, so the response
 * cannot become an enumeration oracle (plan §6.8 · §6.6 · error §3.3). The
 * message is fixed and no `details` / `requestId` may accompany them — the
 * reason belongs in a metric label and the `auth.auth_event` row, not the wire.
 */
export const UNIFORM_ERROR_MESSAGES = {
  UNAUTHENTICATED: 'Unauthorized.',
  RATE_LIMITED: 'Too many requests.',
  PAYLOAD_TOO_LARGE: 'Payload too large.',
} as const;

/**
 * Failure body. Per-code variation is modelled as named optional fields;
 * unknown keys are stripped (no `.passthrough()` / `z.looseObject`).
 * SECURITY_DELISTED requires lifecycle fields (plan §6.6); the three uniform
 * codes above forbid every discretionary field.
 */
export const ApiErrorSchema = z
  .object({
    code: ErrorCodeSchema,
    message: z.string().max(500),
    details: ErrorDetailsSchema,
    canonicalTicker: TickerSchema.optional(),
    lastSeenOn: z.string().min(1).max(32).optional(),
    /** `INTERNAL` only — a per-request id on any other code breaks byte-identity. */
    requestId: z.string().max(128).optional(),
  })
  .superRefine((val, ctx) => {
    if (val.code === 'SECURITY_DELISTED') {
      if (val.canonicalTicker === undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['canonicalTicker'],
          message: 'SECURITY_DELISTED requires canonicalTicker',
        });
      }
      if (val.lastSeenOn === undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['lastSeenOn'],
          message: 'SECURITY_DELISTED requires lastSeenOn',
        });
      }
      return;
    }
    const uniformBodies: Partial<Record<ErrorCode, string>> = UNIFORM_ERROR_MESSAGES;
    const uniform = uniformBodies[val.code];
    if (uniform === undefined) return;
    if (val.message !== uniform) {
      ctx.addIssue({
        code: 'custom',
        path: ['message'],
        message: `${val.code} message must be exactly "${uniform}" (byte-identical bodies)`,
      });
    }
    for (const field of ['details', 'requestId', 'canonicalTicker', 'lastSeenOn'] as const) {
      if (val[field] !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: [field],
          message: `${val.code} bodies carry no ${field} — it would be an enumeration oracle`,
        });
      }
    }
  });
export type ApiError = z.infer<typeof ApiErrorSchema>;

/**
 * Calendar date (US market date) and UTC instant, kept apart on the wire for
 * the same reason the DB keeps `*_on` and `*_at` apart (naming §5.4): the
 * suffix is the only in-code signal of which one a field carries.
 *
 * Shape guard only (`YYYY-MM-DD`); calendar validity (no 2026-02-30) is the
 * ingest layer's contract — this boundary does not invent a calendar.
 */
const MarketDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
const InstantSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/, 'expected an ISO-8601 UTC instant');

/**
 * Array containing each member of a closed tuple EXACTLY once, in any order.
 * `.length(n)` alone constrains COUNT, not content: `['1Y','1Y','1Y','1Y']`
 * satisfies `.length(4)` while advertising one window four times, and /v1/meta
 * is what the SPA builds its pickers from.
 */
function exactlyOnce<T extends string>(schema: z.ZodType<T>, members: readonly T[], label: string) {
  return z
    .array(schema)
    .length(members.length)
    .superRefine((val, ctx) => {
      if (new Set(val).size !== members.length) {
        ctx.addIssue({
          code: 'custom',
          message: `${label} must contain each value exactly once`,
        });
      }
    });
}

/**
 * `/v1/meta` — the freshness oracle (plan §6.6). Lives here rather than in a
 * `meta.schema.ts` because the plan §3.2 file tree is closed and a CI
 * structure-drift check requires every file on disk to appear in it; `/v1/meta`
 * is an API-surface concern, so this is its nearest sanctioned home.
 *
 * Every ingest-derived field is NULLABLE by design, and null means "no
 * published snapshot", never "zero" (R2b). Local/synthetic mode has no ingest
 * at all and reports null across the board; before the first successful
 * production run, `/v1/meta` is the ONE read route that still returns 200 with
 * `asOf: null` so the SPA can render a first-run empty state rather than a
 * generic error (plan §6.6 operational-state contract).
 */
export const DatasetMetaSchema = z.object({
  dataSource: DataSourceSchema,
  /**
   * Never a hard-coded constant — sourced from the adapter's own universe.
   * `0` is valid on the first-run empty path (`asOf: null`, no active securities):
   * positive would force the SPA's documented empty state through VALIDATION
   * instead of a renderable meta payload (plan §6.6 operational-state contract).
   */
  universeSize: z.number().int().nonnegative(),
  /** Latest `ingest_run.status='OK'` snapshot date; null before the first run. */
  asOf: MarketDateSchema.nullable(),
  windows: exactlyOnce(WindowKeySchema, WINDOW_KEYS, 'windows'),
  indices: exactlyOnce(IndexCodeSchema, INDEX_CODES, 'indices'),
  /* Sector rows are objects — uniqueness is on `id`, not reference identity. */
  sectors: z
    .array(SectorSchema)
    .length(SECTOR_IDS.length)
    .superRefine((val, ctx) => {
      if (new Set(val.map((s) => s.id)).size !== SECTOR_IDS.length) {
        ctx.addIssue({
          code: 'custom',
          message: 'sectors must contain each sector id exactly once',
        });
      }
    }),
  lastIngestAt: InstantSchema.nullable(),
  lastDataAdvancedOn: MarketDateSchema.nullable(),
  lastOperationalTradingDay: MarketDateSchema.nullable(),
  membershipAsOf: MarketDateSchema.nullable(),
});
export type DatasetMeta = z.infer<typeof DatasetMetaSchema>;

/**
 * Reserved pagination meta. v1 does not populate it; when it is, `total` is the
 * full filtered set size *before* truncation (plan §6.6).
 */
export const ResponseMetaSchema = z.object({
  total: z.number().int().nonnegative().optional(),
  page: z.number().int().positive().optional(),
  limit: z.number().int().positive().optional(),
});
export type ResponseMeta = z.infer<typeof ResponseMetaSchema>;

/**
 * The concrete schema type the factory returns. Naming it is what lets the
 * factory carry an explicit return type WITHOUT a cast, and keeps `.options`,
 * `.shape` and the real `z.input` — `z.ZodType<…>` erases all three.
 */
export type ApiResponseSchemaOf<T extends z.ZodType> = z.ZodDiscriminatedUnion<
  [
    z.ZodObject<{
      success: z.ZodLiteral<true>;
      data: T;
      meta: z.ZodOptional<typeof ResponseMetaSchema>;
    }>,
    z.ZodObject<{ success: z.ZodLiteral<false>; error: typeof ApiErrorSchema }>,
  ],
  'success'
>;

/** Factory: every route response is `ApiResponse<T>` (plan §6.6 · ADR-0012). */
export function ApiResponseSchema<T extends z.ZodType>(data: T): ApiResponseSchemaOf<T> {
  return z.discriminatedUnion('success', [
    z.object({
      success: z.literal(true),
      data,
      meta: ResponseMetaSchema.optional(),
    }),
    z.object({
      success: z.literal(false),
      error: ApiErrorSchema,
    }),
  ]);
}

/**
 * DERIVED from the schema, not hand-written beside it — the divergence
 * `contracts` exists to prevent. `meta` is `| undefined` rather than
 * exact-optional because Zod emits the key when the input carries
 * `meta: undefined`, and `canonicalQuery` key-sorting keys the ETag.
 */
export type ApiResponse<T> = z.infer<ApiResponseSchemaOf<z.ZodType<T>>>;
